// ════════════════════════════════════════════════════════════════════════════
// CARD EFFECT REGISTRY
// Card-specific effect logic beyond the generic native-keyword mechanics
// already handled in game.js (Repair/Breach/Suppression/Blocker/High-Maneuver)
// is opted into here, one card at a time, keyed by cardNo. Effects are split
// one file per faction color (white.js, green.js, blue.js, red.js, purple.js
// in this same folder) — each calls registerCardEffect() for its own cards.
//
// A card with no registered handler for a given trigger simply has no
// programmatic effect for it — its text is still shown via cleanEffect() in
// the UI for manual adjudication, exactly like every card today.
//
// Available triggers (fired by game.js at the matching point in the existing
// flow) — a card only needs to provide the ones its effect text actually uses:
//   deploy      — unit played from hand (PLAY_CARD) or base deployed (DEPLOY_BASE)
//   unrest      — a playerField unit transitioned rested → active via a card
//                 effect (NOT UNREST_ALL, which is a phase reset). Fired from
//                 setRested() in white.js for player-initiated un-rests, and
//                 from NET.onUnitRest in game.js for opponent-driven network
//                 un-rests. Fires on both the player's and opponent's turns.
//                 ctx.unit is the unit that became active. No onDone — fire and
//                 forget; any async picker is fine since the un-rest is already
//                 applied. Only fires for playerField units (not oppField) so
//                 the picker opens on the correct client.
//   attack      — the unit this Pilot is [During Link] with just attacked
//                 (fires between the Block Step and the Action Step via
//                 fireAttackerPilotAttackTrigger). Only fires on the attacker's
//                 client (attackerSide === 'player') — the opponent's mirrored
//                 flow skips straight to the Action Step. ctx.onDone MUST be
//                 called once the handler finishes (sync or async), so the
//                 Action/Damage Steps can proceed. ctx shape: same as the
//                 battle ctx ({ attacker, attackerSide, defenderSide, defender,
//                 isShield, isDirect, mine, onDone }).
//   defended    — this Unit was just declared as the target of an attack
//                 (fired from registerAction('ATTACK') in game.js, right
//                 after its ctx is built — before the Block/Action/Damage
//                 Steps run). The mirror image of 'attack' above: only fires
//                 on the DEFENDING side's own client (defenderSide ===
//                 'player'), and only for a genuine field Unit target — never
//                 a Base, Shield, or direct-player attack (isShield/isDirect
//                 both false). No onDone — fire and forget; this is a pure
//                 notification, nothing downstream waits on it. ctx shape:
//                 same battle ctx as 'attack' ({ attacker, attackerSide,
//                 defenderSide, defender, isShield, isDirect, mine }).
//   pair        — this Pilot card was just paired with a Unit (PAIR_PILOT).
//                 ctx.unit is the combined unit+pilot now on the field;
//                 ctx.slot is the field slot index. No onDone — fire and
//                 forget; async pickers are fine since pairing is already done.
//   destroyed   — this card was just destroyed in combat (runDamageStep)
//   main        — a plain Command activated via ACTIVATE_COMMAND, after the
//                 confirm-modal's "Activate" click (card is already trashed
//                 by then). ctx.onDone is provided — call it once the
//                 handler's full flow (including any async modal) finishes,
//                 so showCommandActivateModal knows when it's safe to run
//                 maybeAutoEndMainPhase(). A card that registers a 'main'
//                 handler is responsible for calling ctx.onDone() itself —
//                 the caller will NOT call maybeAutoEndMainPhase() on its
//                 own once a handler exists.
//   burst       — this card was flipped from the shield zone as a [Burst]
//                 hit (resolveMyShieldHit). Fires for any card whose
//                 effectRaw contains "[Burst]", AFTER the generic hand/trash
//                 placement already ran (hand only for the literal
//                 "[Burst]Add this card to your hand." pattern; trash
//                 otherwise) — handlers only need to resolve ability text
//                 beyond that placement (e.g. fetching another card from
//                 trash). ctx.picked is the tempDeck entry (cardNo/copyVer)
//                 for the card itself. Handlers must call broadcastCounters()
//                 themselves if they change deck/hand/trash counts, since
//                 this fires after the synchronous broadcastCounters() call
//                 already made by resolveMyShieldHit.
//   activate    — a Base's [Activate･Main] ability, activated via
//                 showBaseActivateModal's "Activate" click, after its cost
//                 (RES / Rest this Base / Exile 1 Command from trash — see
//                 getBaseActivationCost()) has already been paid by the
//                 generic ACTIVATE_BASE dispatch. ctx.onDone is provided —
//                 same convention as 'main': a card with a registered
//                 'activate' handler must call ctx.onDone() itself so the
//                 caller knows when it's safe to run maybeAutoEndMainPhase().
//   action      — a Command/Command Pilot played from hand during the
//                 combat Action Step (resolveActionPlay), after it's already
//                 been paid for, trashed, and counter-synced. ctx.onDone is
//                 provided — call it once the handler's full flow finishes,
//                 same convention as 'main': a card with a registered
//                 'action' handler must call ctx.onDone() itself so
//                 runActionStep knows when it's safe to advance to the next
//                 combat step. ctx.battle is also provided — the live combat
//                 ctx object ({ attacker, attackerSide, defenderSide, mine,
//                 defender, isShield, isDirect }) being resolved by the
//                 current attack's runBlockStep/runActionStep/runDamageStep
//                 pipeline, for effects that need to identify which Units
//                 are actually fighting (e.g. GD02-118/GD02-119's "Unit ...
//                 battling a friendly Unit with Blocker").
//
// Handler signature: (card, ctx) => void
//   card — the live game-card object that owns this effect
//   ctx  — context passed by the trigger call site; shape varies per trigger,
//          see the trigger*( ) call sites in game.js for exactly what's there
//
// passiveUnitApBonus(card, ownerSide) — OPTIONAL hook on a unit's handler
//   (not a trigger). Returns a number added permanently to the unit's AP inside
//   effectiveBp() — evaluated live every AP read, so "while [condition]" passives
//   appear/disappear automatically without any state mutation. `ownerSide` is
//   'player' or 'opp', derived from whether the unit is in playerField or oppField.
//   Example: GD01-081 M1 Astray returns 1 while another (Triple Ship Alliance)
//   Unit is in play.
//
// passiveKeywords(card, ownerSide) — OPTIONAL hook on a unit's handler (not a
//   trigger). Returns an array of keyword strings (e.g. ['Blocker']) that this
//   unit conditionally possesses. Checked live by hasNativeKeyword() alongside
//   nativeKeyword, tempKeywords, and pilotKeywords — so the keyword automatically
//   appears and disappears with field state without any per-turn mutation.
//   Example: GD01-081 M1 Astray returns ['Blocker'] while another (Triple Ship
//   Alliance) Unit is in play.
//
// cantAttack(card) — OPTIONAL hook on a unit's handler (not a trigger).
// Returns true when this card is blocked from attacking due to a conditional
// passive restriction (beyond the generic card.tempCantAttack per-card flag).
// Checked by cardCantAttackNow(card) in game.js — called in all attack-declare
// guards (setupDropZones shield/base/unit click handlers, onCardClick) and in
// renderSlot to apply the 'cant-attack' greyscale CSS class for player cards.
// The hook is only evaluated for playerField cards (never oppField), so it
// safely reads local state. Example: GD03-081 returns !state.superpowerDeployedThisTurn.
//
// cantAttackNonUnit(card) — OPTIONAL hook on a unit's handler (not a trigger).
// Returns true when this card is restricted to attacking enemy Units only —
// it may NOT declare attacks against the base, shield zone, or direct target.
// Checked by cardCantAttackNonUnit(card) in game.js, which is called inside
// the base, direct, and shield zone onclick handlers (setupDropZones /
// renderShields). Clicking a non-unit target while this card is selected logs
// a message and cancels the attack declaration. Unit vs Unit attacks are
// unaffected. Example: ST01-009 Zowort always returns true.
//
// cantBePaired(card) — OPTIONAL hook on a unit's handler (not a trigger).
// Returns true when this card can never be paired with a Pilot at all (a
// permanent restriction printed on the card itself, e.g. "This Unit can't
// be paired with a Pilot"). Checked in handleDrop's occupied-slot branch
// (game.js) before showPairConfirmModal ever opens — dropping a Pilot or
// Command Pilot card onto a restricted Unit logs a message and cancels
// instead. Example: T-014 Ad Balloon, T-022 Wire-Guided Arm.
//
// cantBeSetActive(card) — OPTIONAL hook on a unit's handler (not a trigger).
// Returns true when this card can never become active again once rested (a
// permanent restriction printed on the card itself, e.g. "This Unit can't
// be set as active"). Checked in two places: registerAction('UNREST_ALL', ...)
// (game.js) skips it during the Start Phase's automatic unrest, and
// setRested() (white.js) no-ops any explicit card-effect attempt to set it
// active. Example: T-014 Ad Balloon.
//
// preventsEnemyUnrestByEffect(card) → bool — OPTIONAL hook on a unit's
// handler (not a trigger). Side-wide counterpart to cantBeSetActive above —
// while true, EVERY Unit on the OPPOSING side is blocked from being set
// active by a card effect (not just this one card). Checked inside
// setRested() (white.js) against every card on the field opposing the
// un-rest target, before the Start-Phase-auto-unrest carve-out that
// cantBeSetActive already relies on (setRested isn't called during
// UNREST_ALL at all), so it only ever blocks effect-driven un-resting.
// Example: GD06-037 Reginlaze Julia ("[During Pair] Enemy Units can't be set
// as active by enemy effects") returns true while paired (card.pilot set).
//
// forcedAttackTarget(card, ownerSide) → bool — OPTIONAL hook on a unit's
// handler (not a trigger). Returns true when this unit is currently a forced
// attack target — the opponent MUST declare attacks against it instead of any
// other target (base, shield, direct, or other units). Checked live by
// getForcedAttackTargetUid(defenderSide) in game.js, called from all four
// attack-target click handlers (opp unit in onCardClick, base zone, direct
// zone, shield zone). If the hook returns true for a card in defenderSide's
// field, clicking any other target logs a message and blocks the declaration;
// clicking the forced target unit proceeds normally. The hook runs on the
// ATTACKER's client where the defender's units are in state[defenderSide+'Field']
// — card.rested, card.pilot, and card.trait are all synced to that field, so
// condition checks work correctly. Example: GD03-074 Tieren Taozi returns true
// while rested, paired (card.pilot set), and another (Superpower Bloc) unit
// is on ownerSide's field.
//
// pilotForcedAttackTarget(unitCard, pilotCard, ownerSide, attacker) → bool —
// OPTIONAL hook on a PILOT's handler (not a trigger). Same contract as
// forcedAttackTarget above, but for abilities granted by a paired Pilot to
// whichever Unit it's linked to (rather than a fixed ability on the Unit's
// own card). Checked by getForcedAttackTargetUid(defenderSide, attacker) in
// game.js for every field card with a paired pilot, in addition to the
// unit-level forcedAttackTarget hook. `attacker` is the Unit declaring the
// attack (may be undefined for older call sites) — pass it through so the
// hook can apply attacker-side exclusions. Example: GD05-086 Kayra Su
// ("[During Link] Enemy Units OTHER THAN Link Units choose this rested Unit
// as their attack target if possible") returns true only while the paired
// Unit is rested and isLinkUnit(unitCard) holds, and false whenever the
// attacker itself is a Link Unit (isLinkUnit(attacker)).
//
// onExResourcePlaced(card, {rested}) / onExResourceSpent(card, {amount}) —
// OPTIONAL hooks on a Base's or Unit's handler (not triggers, but fired like
// one — no onDone). placeExResource(rested) in game.js fires
// onExResourcePlaced on state.playerBase and every state.playerField card
// whenever an EX Resource is placed (rested or active). spendResources(cost)
// fires onExResourceSpent the same way whenever paying a cost actually
// consumes one or more ACTIVE EX Resources (exSpent > 0) — NOT when a cost is
// paid entirely from normal RES. Both hooks only fire for the player who owns
// the resource change (never mirrored to the opponent's field, since EX
// Resource counts are local to each side). Example: GD05-018 Gundam Calibarn
// ("When one of your EX Resources is [spent], you may choose 1 of your Units
// — during this turn, reduce enemy damage to it by 3") uses onExResourceSpent.
//
// passiveForceNonTokenDeployRested(card, deployedCard) → bool / forceEnemyDeployRestedThreshold(card) → number —
// OPTIONAL hooks on a unit's handler (not triggers), both checked inside the
// PLAY_CARD action handler right after a new non-token Unit is placed on the
// DEPLOYING player's playerField, before that card object is mirrored
// (unmodified) to the opponent's oppField. passiveForceNonTokenDeployRested
// forces the deploying player's OWN newly played Lv.3-or-lower Units rested
// while a friendly Link Unit with this hook is in play (checked against
// state.playerField). forceEnemyDeployRestedThreshold instead returns a live
// threshold number — every ENEMY Unit's card with this hook is checked
// (against state.oppField, from the deploying player's perspective); if any
// returns a threshold ≥ the newly played Unit's Lv., it deploys rested. Both
// only run once, at the moment of deployment — they do NOT retroactively
// rest already-deployed Units. Example: GD05-026 Gundam Aerial Rebuild
// returns (count of the controller's Units with "Gundam Lfrith"/"Gundnode"
// in their card name, plus itself).
//
// baseRestSubstitute — OPTIONAL boolean flag on a unit's handler (not a
// trigger). When true, this unit may rest in place of the player's Base to
// pay the "Rest this Base" portion of a Base activation cost. Checked by
// showBaseActivateModal in game.js: if cost.restBase is true and an active
// (non-rested) unit with this flag is on playerField, showBaseRestSubstituteModal
// is shown before dispatch('ACTIVATE_BASE') fires. If the player chooses to
// use the substitute, the unit is rested via direct mutation + NET.send
// UNIT_REST and the cost forwarded to ACTIVATE_BASE has restBase: false.
// Only one substitute unit is checked (the first active one found); multiple
// copies of such cards on the field would require extending the lookup.
// Example: GD03-079 G-Defenser.
//
// leagueMilitaireRestSubstitute(base) — OPTIONAL boolean flag on a Base
// handler (not a trigger), forward-declared for GD05-124 White Ark ("During
// your turn, when you would rest a Unit with a friendly (League Militaire)
// Unit's effect, you may rest this Base instead"). NOT YET wired into any
// interception point — no existing card effect currently rests a friendly
// unit as part of its own resolution, so this flag is presently a documented
// no-op. When a future card effect rests a friendly unit as an effect/cost,
// it should call the (not-yet-written) restFriendlyUnitOrSubstitute(target)
// helper instead of setting `target.rested = true` directly; that helper is
// where this flag would actually be checked (mirroring showBaseActivateModal's
// baseRestSubstitute check, but in the opposite direction — Unit target,
// Base substitute). Until such a helper/call site exists, this flag has no
// gameplay effect.
//
// canActivate(card, ctx) — OPTIONAL, registered alongside main/action/activate
// handlers (not a trigger itself). Reports whether the card's effect could
// actually do anything right now — its "If [condition]..." gate is met, and/
// or at least one legal target/candidate exists. A card with no registered
// canActivate defaults to true (the large majority of effects always have
// something to do, e.g. unconditional draw/discard). Checked by
// cardEffectCanActivate(card, ctx) in game.js at every point a player is
// offered the choice to activate a card BEFORE the trigger handler itself
// would run: getActionEligibleHandCards() (Action Step picker), the Main
// Phase Command-drop flow in handleDrop(), canPlayCard() (hand-card dimming),
// and canActivateBase() (Base [Activate･Main] zone clickability) — this is
// what keeps a card like World Distortion ("If 2+ friendly (UN) Units...")
// or Machine Doll Squad ("Choose 1 friendly (Militia)/(Dianna Counter)
// Unit...") from being selectable/activatable at all when its precondition
// can't be met, instead of being chosen and then silently no-op'ing inside
// the handler. ctx is only ever a battle context ({ attacker, attackerSide,
// defenderSide, defender, ... }, same shape as the 'action' trigger's
// ctx.battle) for cards gated on the live Action Step battle (e.g. GD02-118/
// GD02-119) — it's undefined when checked outside an Action Step (Main Phase,
// Base activation). When a handler's own internal "if no candidates, no
// effect" check duplicates canActivate's condition, extract the
// candidate-computation into a shared local function both call, so the two
// can't drift out of sync — see e.g. gd04118Effect/GD04-118's canActivate in
// white.js.
//
// "During this turn" temp modifiers — all cleared by the next UNREST_ALL on
// the affected card's controller (game.js's registerAction('UNREST_ALL', ...)
// and network.js's remoteHandlers.UNREST_ALL):
//   card.tempKeywords    — array of keyword strings (e.g. "gains <Blocker>"),
//                           checked by hasNativeKeyword() alongside nativeKeyword.
//   card.tempApMod       — signed integer AP delta ("during this turn"),
//                           folded into effectiveBp().
//   card.battleApMod     — signed integer AP delta ("during this battle" —
//                           narrower scope than tempApMod), also folded into
//                           effectiveBp(). Cleared at the start of every
//                           runDamageStep resolution (consumed once per
//                           battle) and defensively on UNREST_ALL as a
//                           backstop.
//   card.tempCantAttack  — "can't attack during this turn" lock. Enforced
//                           only in onCardClick's own-field-card-selection
//                           branch (the sole gate through which an attack can
//                           be declared) — a self-only read of the
//                           controller's own authoritative card, so it needs
//                           no network mirroring.
//   card.tempCantReceiveEffectDamage — "can't receive effect damage from
//                           enemy Units during this turn" (e.g. GD04-119).
//                           Currently inert — no existing card effect uses
//                           dealDamageToUnit() with fromEnemy:true. Implement
//                           the check inside dealDamageToUnit() when the first
//                           such card is added.
//   card.baseAbilityUsedThisTurn — set by the generic ACTIVATE_BASE handler
//                           when the activated ability's cost includes
//                           "[Once per Turn]" (see getBaseActivationCost()),
//                           gating canActivateBase() until next turn.
//   card.passiveUsedThisTurn — generic "this Once-per-Turn passive already
//                           fired this turn" flag for non-Activate triggered
//                           abilities (e.g. GD04-129 Willgem's RES-spend
//                           heal) — a card's own handler sets/reads it.
//   card.tempAttackDespiteSickness — "may declare an attack this turn even
//                           though it was just deployed" override, checked by
//                           cardCantAttackNow() alongside the normal
//                           card.rested/summoning-sickness gate. Does NOT by
//                           itself unlock base/shield/direct targeting — pair
//                           with card.tempCantAttackNonUnit to also restrict
//                           it to Unit targets only, if needed (e.g. GD05-030
//                           Michaelis: attacks a rested enemy Unit the turn
//                           it's deployed, but can't hit base/shield/direct
//                           until next turn).
//   card.tempIncomingDamageReduction — flat reduction applied to BOTH battle
//                           damage (read as defTempReduce/attackerTempReduce
//                           inside runDamageStep) and effect damage (read
//                           inside dealDamageToUnit) received from the
//                           enemy during this turn. Unlike battleDefMod
//                           (below), this is turn-scoped, not battle-scoped.
//                           Example: GD05-018 Gundam Calibarn (via
//                           onExResourceSpent) and GD05-125 Ra Cailum's
//                           [Activate･Main].
//   card.tempBattleDamageReduction — same shape as tempIncomingDamageReduction
//                           (turn-scoped, read as defTempBattleReduce/
//                           attackerTempBattleReduce in runDamageStep), but
//                           BATTLE damage only — NOT consulted by
//                           dealDamageToUnit. Use this instead of
//                           tempIncomingDamageReduction when a card's text
//                           specifically says "enemy BATTLE damage", not just
//                           "damage". Example: GD05-094 Quess Paraya's
//                           [Destroyed] grant to a chosen (Neo Zeon) Unit.
//   card.tempCantDirectAttack — "can't choose the enemy player as its attack
//                           target during this turn" — narrower than
//                           tempCantAttackNonUnit: base and shield targeting
//                           are still allowed, only the final direct-attack
//                           target (available once base/shields are both
//                           gone) is blocked. Checked in
//                           showAttackTargetPicker's target-building loop.
//                           Example: GD05-057 Gyunei's Jagd Doga.
//
// cantDirectAttack(card) → bool — OPTIONAL hook on a unit's handler (not a
//   trigger). PERMANENT (not turn-scoped) counterpart to
//   card.tempCantDirectAttack above — same narrower-than-cantAttackNonUnit
//   restriction (base/shield still allowed, only the direct-attack-the-
//   player target is blocked), but for a card whose own printed text is an
//   unconditional standing restriction rather than a granted "this turn"
//   effect. Checked alongside tempCantDirectAttack in the same
//   showAttackTargetPicker branch. Example: GD05-075 Royal Gundam always
//   returns true.
//   card.battleDefMod    — signed integer flat damage reduction "during THIS
//                           battle" (narrower scope than tempIncomingDamageReduction),
//                           applied to both battle damage (runDamageStep) and
//                           effect damage received during that same still-open
//                           battle (dealDamageToUnit). Captured then cleared
//                           for every field card once per battle at the top of
//                           runDamageStep. Example: GD05-022 Gundam Schwarzette's
//                           [Activate･Action].
//
// "Effect damage" — any HP loss that is NOT a Unit's normal combat attack
// damage. Two separate helpers exist:
//
//   dealDamageToBase(side, amount, { fromEnemy }) — for direct HP damage to a
//     Base zone. Covers explicit "Deal N damage" effects and Breach's bonus
//     damage past a destroyed defender. Handles HP loss, destruction
//     (triggering 'destroyed'), and the permanent Base immunity flag below.
//
//   dealDamageToUnit(target, amount, { fromEnemy }) — for direct HP damage to
//     a Unit card on the field. `target` is the live game-card object; `fromEnemy`
//     is true when the damage comes from the TARGET unit's opponent. When
//     fromEnemy:true, the effectDamageReduction hook (see below) on the target's
//     linked pilot is consulted and the amount reduced. Applies the delta and
//     sends FIELD_HP_MOD to mirror to the peer. Does NOT handle unit destruction
//     — check effectiveHp(target) <= 0 after calling if destruction is needed.
//
// effectDamageReduction(card, unit) — OPTIONAL hook on a pilot handler
//   (not a trigger). Returns a number: how many points of ENEMY effect damage
//   aimed at the linked unit are absorbed. Checked dynamically inside
//   dealDamageToUnit() when fromEnemy:true — no state needs to be set on the
//   unit card itself. Because the lookup is live (reads target.pilot at damage
//   time and calls isLinkUnit), the reduction naturally appears and disappears
//   with the pilot's link status. Example: GD04-098 Riddhe Marcenas returns 2.
//
// passiveFieldTokenDamageReduction(pilot, targetToken) — OPTIONAL hook on a
//   pilot handler (not a trigger). Like effectDamageReduction, but checked
//   across EVERY friendly paired pilot on the target's side (not just the
//   target's own pilot) and only when the target is a Unit token
//   (isUnitToken). For "my pilot protects a category of unit regardless of
//   which unit he himself is paired with" effects. Example: GD05-084 Odelo
//   Henrik reduces enemy effect damage to friendly (League Militaire) Unit
//   tokens by 1.
//
// baseEffectDamageBlock(base, targetUnit, amount) — OPTIONAL hook on a Base
//   handler (not a trigger). Unlike effectDamageReduction (flat subtraction),
//   this receives the live incoming `amount` so it can implement threshold
//   effects ("can't receive N-or-less damage" — a full block below the
//   threshold, no effect above it), not just "reduce by N". Checked against
//   the target's own side's Base inside dealDamageToUnit() whenever
//   fromEnemy:true. Returns how many of those points to block. Example:
//   GD05-123 Archangel fully blocks enemy effect damage of 2 or less to
//   friendly (Orb) Units during the opponent's turn:
//   `(base, unit, amount) => isOrbUnit(unit) && !state.myTurn && amount <= 2 ? amount : 0`.
//
// passiveApBonus(pilot, unit, ownerBase, enemyBase) — OPTIONAL hook on a
//   pilot handler (not a trigger). Returns a number added to the
//   paired/linked unit's AP inside effectiveBp(). Evaluated live on every AP
//   read, so "while [condition]" passives automatically appear/disappear
//   without any state mutation. `ownerBase` is the base card belonging to
//   the unit's controlling player, `enemyBase` is the base card belonging to
//   the opposing player (either may be null) — both determined by
//   getPilotPassiveApBonus() from playerField vs oppField so the correct
//   sides are checked on both clients. Examples: GD02-097 Kamille Bidan
//   returns 2 while ownerBase.color === 'white'; GD03-093 Carris Nautilus
//   returns 1 while enemyBase is null (no enemy Base in play, including EX
//   Base — EX Bases occupy the same Base state slot as a normal Base).
//
//   card.cantReceiveEffectDamage — PERMANENT (not turn-scoped, never cleared
//                           by UNREST_ALL) "this Base can't receive enemy
//                           effect damage" immunity (e.g. GD02-129 Argama).
//                           Checked by dealDamageToBase() and by the Breach
//                           branch in runDamageStep only when fromEnemy is
//                           true — self-inflicted effect damage (e.g.
//                           Willgem's own deploy effect) always applies.
//                           Distinct from the turn-scoped, Unit-only
//                           card.tempCantReceiveEffectDamage above.
//
// onAttackDestroysShield(attacker, count) / onLinkedUnitAttackDestroysShield
//   (pilot, attacker, count) / onAnyPlayerAttackDestroysShield(watcher,
//   attacker, count) — OPTIONAL hooks fired from runDamageStep (game.js)
//   whenever a friendly attacker's BATTLE damage (not Breach's bonus
//   overflow — see the Breach branch, which only fires the "Any" watcher
//   version) destroys an enemy shield-area card (a direct shield hit OR the
//   enemy Base, both count as "shield area card"). Checked against, in
//   order: the attacking Unit's own cardNo (e.g. ST03-001 Sinanju), its
//   paired Pilot's cardNo (e.g. GD05-098 Heero Yuy — the pilot-level
//   counterpart, since a pilot ability can't be found via the unit's own
//   cardNo), and every playerField card as a passive watcher regardless of
//   whether it was the attacker (e.g. GD04-042). `count` is 2 for a native
//   <Suppression> double-hit, otherwise 1.
//
// notifyUnitEffectResSpent(side, amount, unit) — fired from
// showUnitActivateModal (game.js) once a field Unit's own [Activate･Main]/
// [Activate･Action] ability cost has been paid. `unit` is the specific Unit
// whose ability was paid for — NOT necessarily the watching card's own
// paired unit (e.g. GD05-101 Gavane Goonny: "if THIS is a (Militia) Unit"
// refers to whichever Unit just paid, checked via unit.trait). Not a
// registry-style per-cardNo hook — instead, a card that cares registers a
// PERMANENT watcher by pushing directly onto unitEffectResSpentWatchers
// (game.js), typically from its own 'pair' trigger so it only watches while
// actually paired (see GD04-100 Sochie Heim, GD05-101 Gavane Goonny in
// white.js for the exact push-a-closure pattern, including the
// passiveUsedThisTurn Once-per-Turn gate and re-verifying the pilot is
// still on the field before firing). Watchers are permanent for the whole
// game session (reset only in initGame()), not turn-scoped.
//
// Exile zone — state.playerExile/state.oppExile arrays hold cards removed
// from the game (e.g. GD04-130's "Exile 1 Command card from your trash"
// cost). The 'exile' tempDeck state is excluded from isInDeck()/deckCount
// just like 'trash'. Exiling a card from MY trash via a Base activation is
// mirrored to the peer via NET.send({ type: 'TRASH_EXILE', uid }) (see
// NET's TRASH_EXILE handler), since only they maintain their own oppTrash
// mirror of my trash contents.
//
// Any tempApMod/battleApMod change — to EITHER side's units — must be
// mirrored to whichever client actually owns the affected uid via
// NET.send({ type: 'FIELD_AP_MOD', uids, delta, field }) (field is
// 'tempApMod' or 'battleApMod'; see FIELD_AP_MOD / NET.onFieldApMod, whose
// receiving handler searches both playerField and oppField). This is
// required (not just cosmetic) because effectiveBp() feeds runDamageStep,
// which both clients run independently for the same shared ATTACK dispatch
// — an un-mirrored mutation on either side desyncs the two clients' damage
// math. The same applies to rested-status changes driven by a card effect
// (as opposed to ordinary combat, which already mirrors via the ATTACK
// dispatch) — mirror via NET.send({ type: 'UNIT_REST', uid, rested }) (see
// NET.onUnitRest), since the real controller's own Block Step eligibility
// check reads their own authoritative field.
//
// Turn-long "if a friendly Unit destroys an enemy Unit with battle damage
// this turn, then..." watchers (e.g. GD03-120): push a callback onto the
// module-level turnDestroyWatchers array in game.js — signature
// (destroyer, destroyed, destroyerSide) => void, destroyerSide is 'player'
// or 'opp' from the local client's perspective. Invoked by
// notifyUnitDestroyedByBattle() from runDamageStep's two Unit-destroyed
// branches (battle damage only, never Breach's bonus Base/shield damage).
// Not removed after firing, since the condition can recur within the same
// turn. Cleared on UNREST_ALL.
//
// onLinkedUnitDestroysEnemy(pilot, unit, destroyedUnit, destroyerSide, destroyerIsAttacker) —
//   OPTIONAL hook on a pilot handler (not a trigger, not turn-scoped) for a
//   standing "when the Unit this Pilot is paired with destroys an enemy Unit
//   with battle damage, then..." passive (e.g. GD02-093 Olba Frost, GD01-094
//   Yzak Jule). Checked live off destroyer.pilot inside
//   notifyUnitDestroyedByBattle() — same call site as turnDestroyWatchers
//   above, same battle-damage-only scope — but always armed for as long as
//   the Pilot is paired, with no register/clear lifecycle. The handler
//   itself is responsible for any "during your turn" gating (check
//   state.myTurn), destroyerSide check, and (if the card text says "Once per
//   Turn") setting/checking pilot.passiveUsedThisTurn — same as a
//   turnDestroyWatchers callback would. destroyerIsAttacker distinguishes
//   "destroyer won as the attacker" (true) from "destroyer won as the
//   defender via counter-damage" (false) — needed by passives gated on
//   "while this Unit is attacking" (e.g. GD01-094), since
//   notifyUnitDestroyedByBattle fires from both of runDamageStep's
//   Unit-destroyed branches.
//
// onLinkedUnitEffectDamageDestroysEnemy(pilot, unit, destroyedUnit) —
//   OPTIONAL hook on a pilot handler (not a trigger). EFFECT-damage
//   counterpart to onLinkedUnitDestroysEnemy above (that one is battle-
//   damage only). Fired from dealDamageToUnit's (js/game.js) enemy-side
//   destroy branch, but ONLY while `unit` (this pilot's paired Unit) is the
//   attacker of a battle currently in progress on this client
//   (state.attackInProgress + currentAttackCtx, set in resolveAttack /
//   cleared in runEpilogue) — i.e. this hook already means "while this Unit
//   is attacking", unlike onLinkedUnitDestroysEnemy's destroyerIsAttacker
//   param, which leaves that check to the handler. dealDamageToUnit has no
//   battle ctx parameter of its own, so currentAttackCtx is what lets it
//   answer "is the Unit that just landed this effect-damage kill also the
//   current attacker" at all. Narrowly scoped to this one shape (not a
//   general "any friendly effect-damage kill" watcher) since it's the only
//   consumer so far — broaden if a future card needs a wider net. Example:
//   ST13-012 Suletta Mercury ("[Once per Turn] When an enemy Unit is
//   destroyed with effect damage while this Unit is attacking, draw 1").
//
// onFriendlyUnitDestroyedByEffect(card, destroyedUnit) — OPTIONAL hook on a
//   unit/base handler (not a trigger). Fires via notifyUnitDestroyedByEffect()
//   (game.js) on the LOCAL player's own base and every remaining playerField
//   card whenever one of their own Units is destroyed by a card EFFECT rather
//   than battle damage — the effect-kill counterpart to
//   notifyUnitDestroyedByBattle/onLinkedUnitDestroysEnemy above. Fires
//   regardless of whether the destruction was self-inflicted (e.g. a
//   sacrifice cost) or enemy-caused (effect damage, an instant-kill effect).
//   Called from three places: dealDamageToUnit's HP≤0 branch, network.js's
//   FIELD_HP_MOD doDestroy, and purple.js's effectDestroyOwnUnit. Example:
//   GD05-054 Alpha Azieru — "[Once per Turn] When one of your Units is
//   destroyed by an effect, draw 1" (gate with card.passiveUsedThisTurn).
//
// effectDestroyOwnUnit(target, sourceCard) — shared helper (purple.js, not
//   part of the registry API) for "Choose 1 of your other Units. Destroy it."
//   effects (e.g. GD05-049/052 Sazabi, GD05-057 Gyunei's Jagd Doga). Unlike
//   effectDestroyEnemyUnit (mutates a mirrored oppField view, relies on a
//   FIELD_HP_MOD round-trip to reach the real owner), this runs directly on
//   the caller's own authoritative playerField, so the destroyed unit's own
//   'destroyed' trigger fires SYNCHRONOUSLY with ctx = { cause: 'effect',
//   sourceCard } — letting a handler check "was I destroyed specifically by
//   one of my controller's own (Neo Zeon)-trait card's effects" via
//   `ctx.cause === 'effect' && (ctx.sourceCard?.trait || '').includes('(Neo
//   Zeon)')` (e.g. GD05-053 Quess's Jagd Doga). Every OTHER existing
//   'destroyed' call site (battle-kill, plain effect damage) passes ctx
//   without a `cause`/`sourceCard` field, so handlers that don't check for it
//   are unaffected. Also sets state.neoZeonEffectDestroyedOwnUnitThisTurn
//   when sourceCard is (Neo Zeon) (GD05-129 Axis's Activate·Main condition;
//   cleared each turn by CLEAR_TURN_EFFECTS/UNREST_ALL).
//
// passiveHpBonus(pilot, unit, ownerBase, enemyBase) — OPTIONAL hook on a
//   pilot handler (not a trigger). HP counterpart to passiveApBonus (same
//   signature) — folded into effectiveHp()/effectiveMaxHp() via
//   getPilotPassiveHpBonus() in game.js. Example: GD05-091 Sting Oakley
//   pairs both hooks for "While an enemy player has 7+ cards in trash, this
//   Unit gets AP+1 and HP+1".
//
// card.tempCantBlock — turn-scoped "can't activate <Blocker> during this
//   turn" restriction, checked alongside hasNativeKeyword(c,'Blocker') in
//   the Block Step's eligible-blocker filter (game.js). Unlike
//   tempCantAttackNonUnit/tempCantDirectAttack (self-only reads of the
//   controller's own field), this one DOES need NET mirroring — the actual
//   Block Step eligibility check always runs on the BLOCKING side's own
//   client against their own state.playerField, so a controller disabling
//   an ENEMY unit's Blocker must mirror it via
//   NET.send({type:'FIELD_AP_MOD', uids, delta:1, field:'tempCantBlock'}).
//   Example: GD05-127 Girty Lue.
//
// state.specialMoveCommandActivatedThisTurn — turn-scoped flag (cleared by
//   CLEAR_TURN_EFFECTS/UNREST_ALL), set inside triggerCardEffect() itself
//   (game.js) whenever a 'main' or 'action' trigger fires on a card with
//   (Special Move) trait — a single choke point covering every activation
//   path (hand Main-activate modal, resolveActionPlay, and a paired pilot's
//   Main triggered mid-attack) without needing to instrument each one.
//   Example: GD05-089 Master Asia's [During Link][Attack] — safe to read
//   directly since an 'attack' trigger only ever fires on the activating
//   player's own client. state.oppSpecialMoveCommandActivatedThisTurn is the
//   mirrored counterpart (set via the SPECIAL_MOVE_COMMAND_ACTIVATED network
//   message sent from inside the same triggerCardEffect check) — read this
//   one instead whenever the check happens inside a hook that's evaluated
//   for a card on EITHER side's field (e.g. a passiveKeywords hook, checked
//   with ownerSide as the discriminator — see GD05-068 Shining Gundam
//   (Super Mode): "gains <Suppression> during this turn" while a (Special
//   Move) Command's Main/Action was activated THIS turn, by THAT card's own
//   controller specifically).
//
// state.causedEnemyDiscardThisTurn / offerDiscardToOpponent(onResult) /
//   forceDiscardOnOpponent() — turn-scoped flag + two shared helpers
//   (game.js) for "force the enemy to discard from their own hand" effects.
//   Only the enemy's own client has authoritative hand data, so both route
//   through a network round-trip (OFFER_DISCARD/DISCARD_RESPONSE for an
//   OPTIONAL discard whose outcome the caller needs — e.g. GD05-034 Gaia
//   Gundam's "if they don't discard, you may deploy..."; FORCE_DISCARD,
//   fire-and-forget, for a MANDATORY discard — e.g. GD05-046 Abyss Gundam
//   (MA Mode)). Both helpers set state.causedEnemyDiscardThisTurn once a
//   discard is confirmed/assumed, consulted by GD05-041 Gaia Gundam (MA
//   Mode)'s handCostReduction hook.
//
// Unit tokens (card.type === 'unit-token', e.g. ST04-012's deployed Strike
// Gundam tokens — see deployTokenToField() in js/game.js) aren't part of any
// deck, so they never enter the trash or hand. Every existing field-exit path
// (combat destroy in runDamageStep, NET.onUnitBounce, the manual
// DISCARD_CARD action, and white.js's bounceEnemyUnit/pickAndBounceOne/Multi
// helpers) already checks isUnitToken(card) and removes a token from the
// game outright instead of trashing/handing it. A paired Pilot is a real
// deck card and is unaffected — it always follows its own normal destination
// regardless of whether the Unit it was paired with was a token. New card
// effects that move a Unit off the field through some other path should
// follow the same isUnitToken() check rather than assuming toTrashCard()/
// hand-placement is always correct.
// ════════════════════════════════════════════════════════════════════════════
window.CardEffects = {};

function registerCardEffect(cardNo, handlers) {
  window.CardEffects[cardNo] = handlers;
}
